import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip,
  CircularProgress, Alert, Snackbar, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import { ShoppingCart, CheckCircle } from '@mui/icons-material';
import axios from '../../utils/CustomAxios';
import { useSelector } from 'react-redux';
import { planPerLabel } from '../../utils/planDuration';

export function SubscriptionPurchase({ planId, onSuccess }) {
  const user = useSelector(state => state.user.user); // Get user from Redux
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [razorpayOrder, setRazorpayOrder] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load subscription plan details
  useEffect(() => {
    if (planId) {
      loadPlan();
    }
  }, [planId]);

  const loadPlan = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`subscriptions/plans/${planId}/`);
      setPlan(response.data);
    } catch (error) {
      showSnackbar('Failed to load subscription plan', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Step 1: Create Razorpay order
  const handleStartPurchase = async () => {
    if (!plan) return;

    // Check if user is authenticated
    if (!user) {
      showSnackbar('Please login to purchase subscription', 'error');
      return;
    }

    try {
      setPurchasing(true);
      showSnackbar('Creating payment order...', 'info');

      // Get inventory_id from user context or use default
      // You might want to get this from props or user selection
      const inventoryId = user?.hub_assignments?.[0] || 1; // Use first assigned hub or default

      const response = await axios.post('subscriptions/checkout/', {
        subscription_plan_id: plan.id,
        inventory_id: inventoryId
      });

      const order = response.data.razorpay_order;
      setRazorpayOrder(order);

      // Step 2: Initialize Razorpay payment
      initializeRazorpayPayment(order, response.data);

    } catch (error) {
      console.error('Checkout error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create payment order';
      showSnackbar(errorMessage, 'error');
      setPurchasing(false);
    }
  };

  // Step 2: Initialize Razorpay SDK
  const initializeRazorpayPayment = (order, orderData) => {
    // Check if Razorpay is loaded
    if (!window.Razorpay) {
      showSnackbar('Razorpay SDK not loaded. Please refresh the page.', 'error');
      setPurchasing(false);
      return;
    }

    // Validate required data
    if (!order || !order.id || !order.amount) {
      showSnackbar('Invalid order data received', 'error');
      setPurchasing(false);
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: order.amount,
      currency: order.currency,
      name: 'AIQ65',
      description: `Subscription: ${orderData.subscription_plan.plan_name}`,
      order_id: order.id, // This is the razorpay_order_id
      handler: function (response) {
        // Step 3: Payment successful - send all required parameters
        handlePaymentSuccess(response, order.id);
      },
      prefill: {
        name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username : '',
        email: user ? user.email : '',
        contact: user ? user.mobile || user.mobileno : '' // Try different mobile field names
      },
      theme: {
        color: '#ED1E79'
      },
      modal: {
        ondismiss: function() {
          setPurchasing(false);
          showSnackbar('Payment cancelled', 'warning');
        }
      }
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Razorpay initialization error:', error);
      showSnackbar('Failed to open payment gateway', 'error');
      setPurchasing(false);
    }
  };

  // Step 3: Handle successful payment - send ALL required parameters
  const handlePaymentSuccess = async (razorpayResponse, orderId) => {
    try {
      showSnackbar('Processing payment...', 'info');

      // Validate Razorpay response
      if (!razorpayResponse.razorpay_payment_id || !razorpayResponse.razorpay_signature) {
        throw new Error('Invalid payment response from Razorpay');
      }

      // Get inventory_id (same as used in checkout)
      const inventoryId = user?.hub_assignments?.[0] || 1;

      const paymentData = {
        razorpay_order_id: orderId, // ✅ REQUIRED - from Step 1
        razorpay_payment_id: razorpayResponse.razorpay_payment_id, // ✅ REQUIRED
        razorpay_signature: razorpayResponse.razorpay_signature, // ✅ REQUIRED
        subscription_plan_id: plan.id,
        inventory_id: inventoryId // Same as used in checkout
      };

      console.log('Sending payment data:', paymentData); // Debug log

      const response = await axios.post('subscriptions/payment/', paymentData);

      // Check if response indicates success
      if (response.data && response.status === 201) {
        showSnackbar('Subscription activated successfully!', 'success');
        setPurchasing(false);

        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
        throw new Error('Payment verification failed');
      }

    } catch (error) {
      console.error('Payment completion error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Payment verification failed';
      showSnackbar(errorMessage, 'error');
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!plan) {
    return (
      <Alert severity="error">Subscription plan not found</Alert>
    );
  }

  const formatBenefits = (plan) => {
    if (plan.benefits_summary) {
      return plan.benefits_summary;
    }

    const benefitCodes = Object.keys(plan.custom_benefits || {});
    if (benefitCodes.length > 0) {
      return benefitCodes
        .map(code => code.split('_').map(part => part.charAt(0) + part.slice(1).toLowerCase()).join(' '))
        .join(', ');
    }

    const benefits = [];
    if (plan.free_delivery) benefits.push('Free Delivery');
    if (plan.double_loyalty_points) benefits.push('2x Loyalty Points');
    if (plan.exclusive_offers) benefits.push('Exclusive Offers');
    return benefits.join(', ') || 'No benefits';
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Subscribe to {plan.plan_name}
          </Typography>

          <Box sx={{ my: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  ₹{plan.base_price}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {/* Was `per 1 months`. Single-unit plans made that visible. */}
                  per {planPerLabel(plan)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Chip
                  label={plan.plan_type}
                  color={plan.plan_type === 'premium' ? 'primary' : 'secondary'}
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>

          <Typography variant="h6" gutterBottom>
            Benefits Included:
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {formatBenefits(plan)}
          </Typography>

          {plan.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {plan.description}
            </Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={purchasing ? <CircularProgress size={20} /> : <ShoppingCart />}
            onClick={handleStartPurchase}
            disabled={purchasing}
            sx={{
              backgroundColor: '#ED1E79',
              '&:hover': { backgroundColor: '#d0176a' },
              py: 1.5
            }}
          >
            {purchasing ? 'Processing...' : `Subscribe for ₹${plan.base_price}`}
          </Button>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
